import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { DicionarioSchema } from 'src/app/services/relatorio-dinamico.service';
import { definirDicionarioSql } from './autocompletar-sql';
import { carregarMonaco } from './monaco-loader';

@Component({
  selector: 'habil-editor-codigo',
  template: `<div #host class="editor-codigo" [style.height]="altura"></div>`,
  styles: [
    `
      :host {
        display: block;
      }

      .editor-codigo {
        width: 100%;
        border: 1px solid #dee2e6;
        border-radius: 6px;
        overflow: hidden;
      }
    `,
  ],
})
export class EditorCodigoComponent implements AfterViewInit, OnChanges, OnDestroy {
  @Input() valor = '';
  @Output() valorChange = new EventEmitter<string>();
  @Output() executar = new EventEmitter<void>();
  @Input() linguagem = 'sql';
  @Input() altura = '360px';
  @Input() somenteLeitura = false;
  @Input() schemaJson: any;
  @Input() nomeArquivo = 'modelo.json';
  @Input() dicionario: DicionarioSchema;

  @ViewChild('host', { static: true }) host: ElementRef<HTMLDivElement>;

  private editor: any;
  private modelo: any;
  private ultimoEmitido: string;

  constructor(private zone: NgZone) {}

  ngAfterViewInit(): void {
    carregarMonaco().then((monaco) => {
      this.zone.runOutsideAngular(() => {
        const uri = monaco.Uri.parse('inmemory://habil/' + this.nomeArquivo + '-' + Math.random().toString(36).slice(2));
        this.modelo = monaco.editor.createModel(this.valor || '', this.linguagem, uri);
        this.aplicarSchema(monaco);
        this.aplicarDicionario(monaco);
        this.editor = monaco.editor.create(this.host.nativeElement, {
          model: this.modelo,
          readOnly: this.somenteLeitura,
          automaticLayout: true,
          minimap: { enabled: false },
          fontSize: 13,
          scrollBeyondLastLine: false,
          tabSize: 2,
          wordWrap: 'on',
        });
        this.editor.addCommand(monaco.KeyMod.CtrlCmd | monaco.KeyCode.Enter, () => this.zone.run(() => this.executar.emit()));
        this.modelo.onDidChangeContent(() => {
          const texto = this.modelo.getValue();
          this.ultimoEmitido = texto;
          this.zone.run(() => this.valorChange.emit(texto));
        });
      });
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!this.modelo) {
      return;
    }
    if (changes.valor && this.valor !== this.ultimoEmitido && this.valor !== this.modelo.getValue()) {
      this.modelo.setValue(this.valor || '');
    }
    if (changes.somenteLeitura && this.editor) {
      this.editor.updateOptions({ readOnly: this.somenteLeitura });
    }
    if (changes.schemaJson) {
      this.aplicarSchema((window as any).monaco);
    }
    if (changes.dicionario) {
      this.aplicarDicionario((window as any).monaco);
    }
  }

  inserir(texto: string): void {
    if (!this.editor) {
      return;
    }
    const selecao = this.editor.getSelection();
    this.editor.executeEdits('habil', [{ range: selecao, text: texto, forceMoveMarkers: true }]);
    this.editor.focus();
  }

  ngOnDestroy(): void {
    this.editor?.dispose();
    this.modelo?.dispose();
  }

  private aplicarDicionario(monaco: any): void {
    if (monaco && this.linguagem === 'sql' && this.dicionario) {
      definirDicionarioSql(monaco, this.dicionario);
    }
  }

  private aplicarSchema(monaco: any): void {
    if (!monaco || this.linguagem !== 'json' || !this.schemaJson || !this.modelo) {
      return;
    }
    monaco.languages.json.jsonDefaults.setDiagnosticsOptions({
      validate: true,
      allowComments: false,
      schemas: [
        {
          uri: 'https://habil.com.br/schemas/' + this.nomeArquivo,
          fileMatch: [this.modelo.uri.toString()],
          schema: this.schemaJson,
        },
      ],
    });
  }
}
